import React, { useEffect, useState } from 'react';
import { getNotifPrefs, setNotifPref, enablePushNotifications, disablePushNotifications, testPush, isThisDeviceSubscribed, NotifPrefs } from './api';

/** Card « Notifications » partagée (Mon Profil + fiche streamer). Agit sur
 *  l'utilisateur connecté (préférences + abonnement push de l'appareil). */
export default function NotificationsCard() {
  const [prefs, setPrefs] = useState<NotifPrefs | null>(null);
  const [meta, setMeta] = useState<{ role?: string; matchs?: boolean; events?: boolean; hasPhone?: boolean }>({});
  const [hasPush, setHasPush] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    // Préférences = partagées par le compte ; abonnement = propre à CET appareil.
    getNotifPrefs().then(r => {
      setPrefs(r.data.prefs);
      setMeta({ role: r.data.role, matchs: r.data.can_manage_matchs, events: r.data.can_manage_events, hasPhone: r.data.has_phone });
    }).catch(() => {});
    isThisDeviceSubscribed().then(setHasPush);
  }, []);

  const toggle = async (key: string, val: boolean) => {
    setPrefs(p => p ? { ...p, [key]: val } : p);
    try { await setNotifPref(key, val); } catch { setPrefs(p => p ? { ...p, [key]: !val } : p); }
  };

  const enable = async () => {
    setBusy(true); setMsg('');
    const r = await enablePushNotifications();
    if (r === 'ok') { setHasPush(true); setMsg('Notifications activées sur cet appareil ✅'); }
    else if (r === 'denied') setMsg('Permission refusée. Autorise les notifications dans les réglages du navigateur.');
    else if (r === 'unsupported') setMsg("Ton navigateur ne supporte pas les notifications. Sur iPhone : ajoute le site à l'écran d'accueil puis réessaie.");
    else setMsg("Échec de l'activation. Réessaie.");
    setBusy(false);
  };

  const disable = async () => {
    setBusy(true); setMsg('');
    await disablePushNotifications();
    setHasPush(false); setMsg('Notifications désactivées sur cet appareil.');
    setBusy(false);
  };

  const test = async () => {
    setMsg('Envoi du test…');
    try { await testPush(); setMsg('Test envoyé ! Tu devrais recevoir une notification 🔔'); }
    catch { setMsg('Le test a échoué (aucun appareil abonné ?).'); }
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 20px' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 14 }}>Notifications</div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 12px', borderRadius: 10, background: hasPush ? '#f0fdf4' : '#f5f3ff', border: `1px solid ${hasPush ? '#bbf7d0' : '#ddd6fe'}`, marginBottom: 16 }}>
        <div style={{ fontSize: 22, lineHeight: 1 }}>{hasPush ? '🔔' : '📱'}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>Notifications sur ce téléphone</div>
          <div style={{ color: '#64748b', fontSize: 12.5, margin: '2px 0 10px' }}>
            {hasPush ? 'Cet appareil reçoit les notifications push.' : 'Active les notifications pour être prévenu même quand l’app est fermée.'}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!hasPush
              ? <button onClick={enable} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>{busy ? 'Activation…' : 'Activer les notifications'}</button>
              : <>
                  <button onClick={test} style={btnSecondary}>Tester 🔔</button>
                  <button onClick={disable} disabled={busy} style={{ ...btnSecondary, color: '#dc2626', borderColor: '#fecaca' }}>Désactiver</button>
                </>}
          </div>
          {msg && <div style={{ marginTop: 10, fontSize: 12.5, color: '#475569' }}>{msg}</div>}
        </div>
      </div>

      {prefs && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Row label="Nouveaux messages" desc="Réglage par défaut de tous les groupes, conversations et canaux (modifiable dans chaque chat via 🔔)" checked={prefs.new_message} onChange={v => toggle('new_message', v)} />
          <div style={{ paddingLeft: 18, borderLeft: '2px solid #ede9fe', marginLeft: 4 }}>
            <Row label="Seulement quand je suis cité (@)" desc="Par défaut, ne notifier que les messages où l’on me mentionne" checked={prefs.new_message_mention_only} disabled={!prefs.new_message} onChange={v => toggle('new_message_mention_only', v)} />
          </div>
          <Row label="Nouvel évènement" checked={prefs.new_event} onChange={v => toggle('new_event', v)} />
          <Row label="Nouveau match" checked={prefs.new_match} onChange={v => toggle('new_match', v)} />
          <Row label="Nouvelles ressources" checked={prefs.new_resource} onChange={v => toggle('new_resource', v)} />
          <Row label="Demande de match acceptée" checked={prefs.match_accepted} onChange={v => toggle('match_accepted', v)} />
          <Row label="Demande d’évènement acceptée" checked={prefs.event_accepted} onChange={v => toggle('event_accepted', v)} />
          <Row label="Objectif atteint" checked={prefs.goal_reached} onChange={v => toggle('goal_reached', v)} />
        </div>
      )}

      {prefs && (meta.role === 'admin' || meta.matchs || meta.events) && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
          <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Notifications SMS</div>
          {!meta.hasPhone && (
            <div style={{ fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
              ⚠️ Renseigne un numéro de téléphone dans ton profil pour recevoir ces SMS.
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {meta.matchs && <Row label="Nouvelle demande de match" desc="SMS quand un streamer soumet une demande de match" checked={prefs.sms_match_request} onChange={v => toggle('sms_match_request', v)} />}
            {meta.events && <Row label="Nouvelle demande d’évènement" desc="SMS quand un streamer soumet une demande d’évènement" checked={prefs.sms_event_request} onChange={v => toggle('sms_event_request', v)} />}
            {meta.role === 'admin' && <>
              <Row label="Nouveau message (vitrine)" desc="Formulaire de contact du site vitrine" checked={prefs.sms_new_message} onChange={v => toggle('sms_new_message', v)} />
              <Row label="Nouvelle candidature" desc="Formulaire « devenir tiktokeur / agent »" checked={prefs.sms_new_application} onChange={v => toggle('sms_new_application', v)} />
              <Row label="Nouveau chat visiteur (vitrine)" desc="Un visiteur démarre un chat support" checked={prefs.sms_new_support_chat} onChange={v => toggle('sms_new_support_chat', v)} />
            </>}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, desc, checked, onChange, disabled }: { label: string; desc?: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 2px', borderBottom: '1px solid #f1f5f9', opacity: disabled ? 0.5 : 1 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#0f172a', fontSize: 13.5, fontWeight: 500 }}>{label}</div>
        {desc && <div style={{ color: '#94a3b8', fontSize: 11.5, marginTop: 1 }}>{desc}</div>}
      </div>
      <button role="switch" aria-checked={checked} disabled={disabled} onClick={() => !disabled && onChange(!checked)}
        style={{ flexShrink: 0, width: 44, height: 25, borderRadius: 13, border: 'none', position: 'relative', cursor: disabled ? 'default' : 'pointer', background: checked ? '#7c3aed' : '#cbd5e1', transition: 'background .15s' }}>
        <span style={{ position: 'absolute', top: 3, left: checked ? 22 : 3, width: 19, height: 19, borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)' }} />
      </button>
    </div>
  );
}

const btnPrimary: React.CSSProperties = { padding: '9px 18px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { padding: '9px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer' };
